import React, { useState, useEffect } from 'react';
import { Empleado, Departamento } from '../tipos/vacaciones';
import * as empleadoService from '../services/empleadoService';
import './FormularioEmpleado.css';

interface FormularioEmpleadoProps {
  empleado?: Empleado | null;
  departamentos: Departamento[];
  onGuardar: () => void;
  onCancelar: () => void;
}

const FormularioEmpleado: React.FC<FormularioEmpleadoProps> = ({
  empleado,
  departamentos,
  onGuardar,
  onCancelar,
}) => {
  const esEdicion = !!empleado;

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [departamentoId, setDepartamentoId] = useState<number>(0);
  const [cargo, setCargo] = useState('');
  const [fechaIngreso, setFechaIngreso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorServidor, setErrorServidor] = useState<string | null>(null);
  const [erroresValidacion, setErroresValidacion] = useState<Record<string, string>>({});

  useEffect(() => {
    if (empleado) {
      setNombre(empleado.nombre);
      setEmail(empleado.email);
      setCargo(empleado.cargo);
      setFechaIngreso(empleado.fechaIngreso.split('T')[0]);
      // Buscar el departamentoId por nombre
      const dept = departamentos.find(d => d.nombre === empleado.departamento);
      setDepartamentoId(dept?.id || 0);
    }
  }, [empleado, departamentos]);

  const validarFormulario = (): boolean => {
    const errores: Record<string, string> = {};

    if (!nombre.trim()) {
      errores.nombre = 'El nombre es requerido';
    }

    if (!email.trim()) {
      errores.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errores.email = 'El formato del email no es valido';
    }

    if (!departamentoId || departamentoId === 0) {
      errores.departamentoId = 'Debe seleccionar un departamento';
    }

    if (!cargo.trim()) {
      errores.cargo = 'El cargo es requerido';
    }

    if (!fechaIngreso) {
      errores.fechaIngreso = 'La fecha de ingreso es requerida';
    } else if (new Date(fechaIngreso + 'T00:00:00') > new Date()) {
      errores.fechaIngreso = 'La fecha de ingreso no puede ser una fecha futura';
    }

    setErroresValidacion(errores);
    return Object.keys(errores).length === 0;
  };

  const manejarEnvio = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorServidor(null);

    if (!validarFormulario()) return;

    setGuardando(true);

    try {
      if (esEdicion && empleado) {
        await empleadoService.update(empleado.id, {
          nombre: nombre.trim(),
          email: email.trim(),
          departamentoId,
          cargo: cargo.trim(),
          fechaIngreso,
          estaActivo: empleado.estaActivo,
        });
      } else {
        await empleadoService.create({
          nombre: nombre.trim(),
          email: email.trim(),
          departamentoId,
          cargo: cargo.trim(),
          fechaIngreso,
        });
      }
      onGuardar();
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : 'Error al guardar el empleado';
      setErrorServidor(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  const manejarClickOverlay = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !guardando) {
      onCancelar();
    }
  };

  return (
    <div className="modal-overlay" onClick={manejarClickOverlay}>
      <div className="modal-formulario">
        <div className="modal-formulario-header">
          <h3>
            <i className={`fas ${esEdicion ? 'fa-user-edit' : 'fa-user-plus'}`}></i>
            {esEdicion ? 'Editar Empleado' : 'Nuevo Empleado'}
          </h3>
        </div>

        <form className="modal-formulario-body" onSubmit={manejarEnvio}>
          <div className="campo-formulario">
            <label htmlFor="emp-nombre">Nombre *</label>
            <input
              id="emp-nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre completo"
              disabled={guardando}
            />
            {erroresValidacion.nombre && (
              <span className="error-campo">{erroresValidacion.nombre}</span>
            )}
          </div>

          <div className="campo-formulario">
            <label htmlFor="emp-email">Email *</label>
            <input
              id="emp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              disabled={guardando}
            />
            {erroresValidacion.email && (
              <span className="error-campo">{erroresValidacion.email}</span>
            )}
          </div>

          <div className="campo-formulario">
            <label htmlFor="emp-departamento">Departamento *</label>
            <select
              id="emp-departamento"
              value={departamentoId}
              onChange={(e) => setDepartamentoId(Number(e.target.value))}
              disabled={guardando}
            >
              <option value={0}>Seleccionar departamento...</option>
              {departamentos.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.nombre}</option>
              ))}
            </select>
            {erroresValidacion.departamentoId && (
              <span className="error-campo">{erroresValidacion.departamentoId}</span>
            )}
          </div>

          <div className="campo-formulario">
            <label htmlFor="emp-cargo">Cargo *</label>
            <input
              id="emp-cargo"
              type="text"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              placeholder="Cargo del empleado"
              disabled={guardando}
            />
            {erroresValidacion.cargo && (
              <span className="error-campo">{erroresValidacion.cargo}</span>
            )}
          </div>

          <div className="campo-formulario">
            <label htmlFor="emp-fecha-ingreso">Fecha de Ingreso *</label>
            <input
              id="emp-fecha-ingreso"
              type="date"
              value={fechaIngreso}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setFechaIngreso(e.target.value)}
              disabled={guardando}
            />
            {erroresValidacion.fechaIngreso && (
              <span className="error-campo">{erroresValidacion.fechaIngreso}</span>
            )}
          </div>

          {errorServidor && (
            <div className="error-servidor">
              <i className="fas fa-exclamation-circle"></i>
              {errorServidor}
            </div>
          )}

          <div className="modal-formulario-footer">
            <button
              type="button"
              className="btn-modal btn-cancelar"
              onClick={onCancelar}
              disabled={guardando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-modal btn-guardar"
              disabled={guardando}
            >
              {guardando ? (
                <>
                  <span className="btn-spinner"></span>
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioEmpleado;
